import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const actes = [
  {
    num: 1,
    title: "LE MONDE UNIPOLAIRE",
    text: `Pour comprendre le monde qui arrive, il faut d'abord comprendre celui qui est en train de disparaître.
Cet acte revient sur la construction d'un ordre mondial qui, pendant plusieurs décennies, a organisé une grande partie du commerce, de la monnaie, de l'énergie, de la sécurité et de la technologie autour d'un même centre de gravité.
L'objectif est de comprendre comment cette domination s'est construite, quels mécanismes l'ont rendue possible et pourquoi elle a fini par façonner presque tous les grands équilibres économiques et géopolitiques modernes.
C'est le point de départ indispensable pour comprendre ce qui est aujourd'hui en train de changer.`,
  },
  {
    num: 2,
    title: "LE MONDE MULTIPOLAIRE",
    text: `L'ordre mondial ne disparaît pas. Il se réorganise.
La puissance économique, industrielle, militaire et technologique se répartit désormais entre plusieurs pôles capables de défendre leurs propres intérêts, de construire leurs propres dépendances et de contester certaines règles établies depuis des décennies.
Cet acte montre comment cette redistribution de la puissance transforme les rapports entre États, les flux commerciaux, les monnaies, les chaînes d'approvisionnement, la défense et les politiques industrielles.
Comprendre ce nouvel équilibre, c'est comprendre dans quelle direction le monde est en train de se déplacer.`,
  },
  {
    num: 3,
    title: "LES SECTEURS CRITIQUES",
    text: `Le monde multipolaire ne change pas seulement les rapports de force entre États.
Il redirige aussi les capitaux, les politiques industrielles et les priorités stratégiques.
Dans le même temps, la course technologique s'accélère.
Et lorsque ces deux dynamiques se croisent, certains secteurs bien précis se retrouvent soudain au centre de milliards de dollars d'investissements, de subventions, de tensions commerciales et de stratégies nationales.
L'enjeu n'est donc pas seulement de repérer ces secteurs. Il faut comprendre pourquoi ils deviennent critiques, quels rapports de force ils concentrent, quelles dépendances ils révèlent et où se situent réellement les opportunités.
Ce sont ces secteurs qui concentreront une partie majeure de la création de valeur de la prochaine décennie.`,
  },
  {
    num: 4,
    title: "LES ENTREPRISES STRATÉGIQUES",
    text: `Après avoir identifié les secteurs qui concentreront une partie majeure de la création de valeur de la prochaine décennie, une autre question devient essentielle pour un investisseur : quelles entreprises sont réellement les mieux positionnées pour en profiter ?
Toutes ne se valent pas.
Certaines disposent d'avantages difficiles à voir au premier regard, de positions dominantes dans leur chaîne de valeur, de technologies sous-estimées ou d'un rôle devenu indispensable à mesure que ces secteurs accélèrent.
L'enjeu est donc d'identifier celles qui captent le plus de valeur, celles que le marché comprend encore mal et celles qui pourraient bénéficier le plus directement des transformations en cours.`,
  },
  {
    num: 5,
    title: "LA MÉTHODE",
    text: `MULTIPOLAR ne s'arrête pas avec le dernier épisode. Le monde continuera de bouger, de nouveaux événements apparaîtront, de nouvelles tensions aussi.
L'objectif de cet acte est donc de vous apprendre à voir comme je vois : lire correctement ce qui se passe, comprendre ce que cela implique, et identifier quels secteurs ou quelles entreprises pourraient en profiter.
Cet acte rassemble la méthode utilisée tout au long de MULTIPOLAR : partir du contexte global, comprendre les dynamiques sectorielles, analyser les entreprises, repérer les signaux importants et construire une lecture cohérente avant de prendre une décision.
L'objectif est de transformer toute l'analyse précédente en un processus concret, structuré et reproductible.`,
  },
];

const calendrier = [
  { actes: "Actes 1 & 2", date: "30 septembre 2026" },
  { actes: "Acte 3", date: "31 octobre 2026" },
  { actes: "Acte 4", date: "30 novembre 2026" },
  { actes: "Acte 5", date: "31 décembre 2026" },
];

export default function Programme() {
  return (
    <section id="programme" data-testid="programme-section" className="py-24 md:py-32 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-14 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div>
            <span className="micro-label">Le programme</span>
            <h2 className="font-display text-4xl sm:text-5xl leading-[1.05] mt-6">
              Cinq actes,<br />une méthode.
            </h2>
          </div>
          <p className="max-w-md text-[#D8D4CC] font-light">
            Un programme vidéo en cinq actes. Chaque acte s'ajoute à votre espace membre
            le jour de sa sortie et reste accessible à vie.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <Accordion type="single" collapsible className="w-full">
              {actes.map((a) => (
                <AccordionItem key={a.num} value={`acte-${a.num}`} className="border-white/10" data-testid={`acte-item-${a.num}`}>
                  <AccordionTrigger
                    data-testid={`acte-trigger-${a.num}`}
                    className="text-left font-mono text-[10px] tracking-[0.06em] leading-tight md:text-sm md:tracking-[0.15em] uppercase text-[#F5F5F0] hover:text-[#FF6B00] hover:no-underline py-6"
                  >
                    Acte {a.num} - {a.title}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#D8D4CC] font-light pb-8 max-w-[70ch]">
                    {a.text.split("\n").map((line, i) => (
                      <p key={i} className="mb-[1em] last:mb-0">{line}</p>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          <motion.aside
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-5"
            data-testid="calendrier-sortie"
          >
            <div className="border border-white/10 bg-white/[0.02] rounded-sm p-8">
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#758D85]">
                Calendrier de sortie
              </span>
              <div className="mt-8 space-y-7">
                {calendrier.map((c) => (
                  <div key={c.actes}>
                    <div className="font-mono text-sm tracking-[0.15em] uppercase text-[#FF6B00]">
                      {c.actes}
                    </div>
                    <div className="mt-1 text-[#D8D4CC] font-light text-[15px]">{c.date}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
